import React from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ExclamationCircleOutlined,
  HistoryOutlined,
  ReloadOutlined,
  ToolOutlined,
} from '@ant-design/icons';

import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import useRuntimeMutation from '../../hooks/useRuntimeMutation';
import useRuntimeAutoRefresh from '../../hooks/useRuntimeAutoRefresh';
import {
  runtimeDiagnosticsService,
  runtimeRecoveryOperationsService,
} from '../../services/runtimeOperationalService';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import { buildApiErrorMessage } from '../../utils/apiError';
import {
  buildManualRecoveryPayload,
  computeAgingProgress,
  extractTimelineOperationalContext,
  filterByFulfillment,
  generateRecoveryCorrelationId,
  getActionAvailability,
  getReasonCodePresets,
  getSeverityTone,
  sortBySeverityAndAge,
} from './recoveryConsole.utils';

const { Title, Text } = Typography;

const AUTO_REFRESH_INTERVAL_MS = 120000;

const severityColor = {
  normal: 'green',
  warning: 'gold',
  critical: 'red',
};

const classificationColor = {
  transient: 'blue',
  operator: 'orange',
  terminal: 'magenta',
};

const REQUEST_STATUS_COLOR = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
};

const REQUEST_STATUS_LABEL = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Rejeitado',
  CANCELLED: 'Cancelado',
};

const REQUEST_TYPE_LABEL = {
  UPGRADE: 'Upgrade',
  DOWNGRADE: 'Downgrade',
  CANCEL: 'Cancelamento',
};

const FULFILLMENT_LABEL = {
  NOT_STARTED: 'Nao iniciado',
  PAYMENT_IN_PROGRESS: 'Pagamento em andamento',
  PAID_PENDING_APPLY: 'Pago aguardando aplicacao',
  EFFECTUATED: 'Efetivado',
  FAILED: 'Falhou',
  NOOP_TERMINAL: 'No-op terminal',
};

const FULFILLMENT_COLOR = {
  NOT_STARTED: 'default',
  PAYMENT_IN_PROGRESS: 'processing',
  PAID_PENDING_APPLY: 'gold',
  EFFECTUATED: 'green',
  FAILED: 'red',
  NOOP_TERMINAL: 'default',
};

const ACTIONS = {
  replay_safe: {
    label: 'Replay Safe',
    buttonType: 'default',
    execute: (requestId, payload) => runtimeRecoveryOperationsService.replaySafe(requestId, payload),
    requiresClassification: false,
    tooltip: 'Recoloca request em PAYMENT_IN_PROGRESS quando elegivel.',
  },
  mark_dead_letter: {
    label: 'Mark Dead-Letter',
    buttonType: 'default',
    execute: (requestId, payload) => runtimeRecoveryOperationsService.markDeadLetter(requestId, payload),
    requiresClassification: false,
    tooltip: 'Marca request em dead-letter logico (FAILED + DEAD_LETTER_*).',
  },
  reopen_dead_letter: {
    label: 'Reopen Dead-Letter',
    buttonType: 'default',
    execute: (requestId, payload) => runtimeRecoveryOperationsService.reopenDeadLetter(requestId, payload),
    requiresClassification: false,
    tooltip: 'Reabre request em dead-letter para retomar fluxo controlado.',
  },
  reclassify_recovery: {
    label: 'Reclassify',
    buttonType: 'default',
    execute: (requestId, payload) => runtimeRecoveryOperationsService.reclassify(requestId, payload),
    requiresClassification: true,
    tooltip: 'Atualiza classificacao operacional sem alterar semantics de fulfillment.',
  },
  manual_reissue_prepare_payment: {
    label: 'Reissue Prepare-Payment',
    buttonType: 'primary',
    execute: (requestId, payload) => runtimeRecoveryOperationsService.reissuePreparePayment(requestId, payload),
    requiresClassification: false,
    tooltip: 'Reexecuta prepare-payment de forma manual e auditavel.',
  },
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const RecoveryActionModal = ({ open, context, loading, onCancel, onConfirm }) => {
  const [form] = Form.useForm();
  const reasonCodePresets = React.useMemo(
    () => getReasonCodePresets(context?.actionKey),
    [context?.actionKey]
  );

  React.useEffect(() => {
    if (!open || !context) {
      return;
    }
    form.setFieldsValue({
      reason_code: reasonCodePresets[0] || '',
      correlation_id: '',
      classification: context.action?.requiresClassification ? 'operator' : undefined,
      confirm: false,
    });
  }, [open, context, form, reasonCodePresets]);

  const handleGenerateCorrelationId = () => {
    const generated = generateRecoveryCorrelationId(context?.item?.request_id);
    form.setFieldValue('correlation_id', generated);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.confirm !== true) {
        message.warning('Confirmacao explicita obrigatoria para executar a operacao.');
        return;
      }

      const payload = buildManualRecoveryPayload({
        reasonCode: values.reason_code,
        correlationId: values.correlation_id,
        classification: context?.action?.requiresClassification ? values.classification : undefined,
      });

      await onConfirm(payload);
      form.resetFields();
    } catch (_error) {
      // validation state handled by Form and action callback.
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText="Executar operacao"
      confirmLoading={loading}
      title={context ? `Confirmar acao: ${context.action.label}` : 'Confirmar acao'}
      okButtonProps={{ danger: context?.action?.buttonType === 'primary' }}
      destroyOnHidden
    >
      {context?.item ? (
        <Alert
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          message="Operacao manual governada"
          description={`request_id=${context.item.request_id} | tenant_id=${context.item.tenant_id} | fulfillment=${context.item.fulfillment}`}
        />
      ) : null}

      <Form form={form} layout="vertical">
        <Form.Item
          label="reason_code"
          name="reason_code"
          rules={[{ required: true, message: 'reason_code e obrigatorio' }]}
          tooltip="Use um preset recomendado para manter trilha operacional consistente."
        >
          <AutoComplete
            options={reasonCodePresets.map((value) => ({ value }))}
            placeholder="Ex: RECOVERY_OPERATOR_CONFIRMATION"
            filterOption={(inputValue, option) =>
              String(option?.value || '').toUpperCase().includes(String(inputValue || '').toUpperCase())
            }
          />
        </Form.Item>

        <Space wrap style={{ marginBottom: 12 }}>
          {reasonCodePresets.map((preset) => (
            <Button
              key={preset}
              size="small"
              onClick={() => form.setFieldValue('reason_code', preset)}
            >
              {preset}
            </Button>
          ))}
        </Space>

        <Form.Item
          label="correlation_id"
          name="correlation_id"
          rules={[{ required: true, message: 'correlation_id e obrigatorio' }]}
          tooltip="Opcionalmente gere um correlation_id assistido para rastreabilidade ponta-a-ponta."
        >
          <Input
            placeholder="Correlation id da operacao"
            maxLength={120}
            addonAfter={(
              <Button size="small" onClick={handleGenerateCorrelationId}>
                Gerar
              </Button>
            )}
          />
        </Form.Item>

        {context?.action?.requiresClassification ? (
          <Form.Item
            label="classification"
            name="classification"
            rules={[{ required: true, message: 'classification e obrigatoria para reclassify' }]}
          >
            <Select
              options={[
                { value: 'transient', label: 'transient' },
                { value: 'operator', label: 'operator' },
                { value: 'terminal', label: 'terminal' },
              ]}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          name="confirm"
          valuePropName="checked"
          rules={[
            {
              validator: (_, checked) =>
                checked ? Promise.resolve() : Promise.reject(new Error('Confirmacao explicita obrigatoria')),
            },
          ]}
        >
          <Checkbox>
            Confirmo execucao manual governada desta acao.
          </Checkbox>
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PlatformRecoveryOperationsConsole = () => {
  const [tenantId, setTenantId] = React.useState('');
  const [selectedSeverities, setSelectedSeverities] = React.useState([]);
  const [selectedClassifications, setSelectedClassifications] = React.useState([]);
  const [selectedFulfillment, setSelectedFulfillment] = React.useState('');
  const [autoRefreshEnabled, setAutoRefreshEnabled] = React.useState(false);
  const [lastRefreshAt, setLastRefreshAt] = React.useState(null);
  const [actionContext, setActionContext] = React.useState(null);
  const [timelineOpen, setTimelineOpen] = React.useState(false);
  const [timelineRequest, setTimelineRequest] = React.useState(null);
  const [timelineData, setTimelineData] = React.useState([]);
  const [timelineLoading, setTimelineLoading] = React.useState(false);
  const [timelineError, setTimelineError] = React.useState('');

  const stuckFilters = React.useMemo(
    () => ({
      tenant_id: String(tenantId || '').trim() || undefined,
      severities: selectedSeverities.length > 0 ? selectedSeverities : undefined,
      classifications: selectedClassifications.length > 0 ? selectedClassifications : undefined,
      limit: 200,
      offset: 0,
    }),
    [tenantId, selectedSeverities, selectedClassifications]
  );

  const summaryFilters = React.useMemo(
    () => ({
      tenant_id: String(tenantId || '').trim() || undefined,
    }),
    [tenantId]
  );

  const stuckQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.recoveryStuckRequests(stuckFilters),
    queryFn: async () => {
      const response = await runtimeDiagnosticsService.getRecoveryStuckRequests(stuckFilters);
      return response.data;
    },
  });

  const summaryQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.recoverySummary(summaryFilters),
    queryFn: async () => {
      const response = await runtimeDiagnosticsService.getRecoverySummary(summaryFilters);
      return response.data;
    },
  });

  const agingQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.recoveryAgingBuckets(summaryFilters),
    queryFn: async () => {
      const response = await runtimeDiagnosticsService.getRecoveryAgingBuckets(summaryFilters);
      return response.data;
    },
  });

  const operationMutation = useRuntimeMutation({
    mutationFn: async ({ requestId, actionKey, payload }) => {
      const action = ACTIONS[actionKey];
      if (!action) {
        throw new Error('Acao manual desconhecida');
      }
      const response = await action.execute(requestId, payload);
      return response.data;
    },
    invalidateKeys: [runtimeQueryKeys.root],
  });

  const refreshAll = React.useCallback(async () => {
    await Promise.allSettled([
      stuckQuery.refetch(),
      summaryQuery.refetch(),
      agingQuery.refetch(),
    ]);
    setLastRefreshAt(new Date().toISOString());
  }, [stuckQuery, summaryQuery, agingQuery]);

  useRuntimeAutoRefresh({
    enabled: autoRefreshEnabled,
    intervalMs: AUTO_REFRESH_INTERVAL_MS,
    pauseWhenHidden: true,
    jitterRatio: 0.1,
    onRefresh: refreshAll,
  });

  const filteredItems = React.useMemo(
    () => {
      const rawItems = Array.isArray(stuckQuery.data?.items) ? stuckQuery.data.items : [];
      return sortBySeverityAndAge(filterByFulfillment(rawItems, selectedFulfillment));
    },
    [stuckQuery.data?.items, selectedFulfillment]
  );

  const fulfillmentOptions = React.useMemo(() => {
    const byFulfillment = summaryQuery.data?.summary?.by_fulfillment || {};
    const keys = Object.keys(byFulfillment || {});
    return keys.map((key) => ({ label: key, value: key }));
  }, [summaryQuery.data?.summary?.by_fulfillment]);

  const openActionModal = (item, actionKey) => {
    const availability = getActionAvailability(item, actionKey);
    if (!availability.enabled) {
      const firstBlocker = availability.blockers[0] || 'Acao bloqueada pelo estado atual da request.';
      message.warning(`Acao bloqueada: ${firstBlocker}`);
      return;
    }
    setActionContext({ item, actionKey, action: ACTIONS[actionKey], availability });
  };

  const closeActionModal = () => {
    if (operationMutation.isPending) {
      return;
    }
    setActionContext(null);
  };

  const executeManualAction = async (payload) => {
    if (!actionContext?.item || !actionContext?.actionKey) {
      return;
    }
    try {
      const result = await operationMutation.mutateAsync({
        requestId: actionContext.item.request_id,
        actionKey: actionContext.actionKey,
        payload,
      });
      message.success(result?.message || 'Operacao manual executada com sucesso.');
      setActionContext(null);
    } catch (error) {
      const parsed = buildApiErrorMessage(error, 'Falha ao executar acao manual de recovery.');
      message.error(parsed.message);
    }
  };

  const openTimeline = async (item) => {
    setTimelineRequest(item);
    setTimelineOpen(true);
    setTimelineLoading(true);
    setTimelineError('');

    try {
      const timelineResponse = await runtimeRecoveryOperationsService.getPaymentCaseTimeline({
        request_id: item.request_id,
        tenant_id: item.tenant_id,
        categories: ['runtime', 'transition', 'operational', 'payment_intent', 'charge', 'invoice'],
        include_payload: true,
      });
      const timeline = Array.isArray(timelineResponse?.timeline) ? timelineResponse.timeline : [];
      setTimelineData(timeline);
    } catch (error) {
      const parsed = buildApiErrorMessage(error, 'Nao foi possivel carregar timeline operacional.');
      setTimelineError(parsed.message);
      setTimelineData([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const summaryData = summaryQuery.data?.summary || {};
  const severitySummary = summaryData.by_severity || {};
  const classificationSummary = summaryData.by_classification || {};
  const agingBuckets = Array.isArray(agingQuery.data?.buckets) ? agingQuery.data.buckets : [];
  const agingSummary = agingQuery.data?.summary || {};

  const renderActionButton = (row, actionKey) => {
    const action = ACTIONS[actionKey];
    const availability = getActionAvailability(row, actionKey);
    const button = (
      <Button
        size="small"
        type={action.buttonType === 'primary' ? 'primary' : 'default'}
        disabled={!availability.enabled}
        onClick={() => openActionModal(row, actionKey)}
      >
        {actionKey === 'replay_safe' ? 'replay-safe' : null}
        {actionKey === 'mark_dead_letter' ? 'mark dead-letter' : null}
        {actionKey === 'reopen_dead_letter' ? 'reopen dead-letter' : null}
        {actionKey === 'reclassify_recovery' ? 'reclassify' : null}
        {actionKey === 'manual_reissue_prepare_payment' ? 'reissue prepare-payment' : null}
      </Button>
    );

    const tooltipText = availability.enabled
      ? action.tooltip
      : `Bloqueado: ${availability.blockers.join(' | ')}`;

    return (
      <Tooltip title={tooltipText}>
        <span>{button}</span>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 220,
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: 'Request',
      key: 'request',
      width: 210,
      render: (_, row) => (
        <Space direction="vertical" size={2}>
          <Text strong>{row.request_id}</Text>
          <Space size={4} wrap>
            <Tag color="blue">{REQUEST_TYPE_LABEL[row.request_type] || row.request_type || '-'}</Tag>
            <Tag color={REQUEST_STATUS_COLOR[row.request_status] || 'default'}>
              {REQUEST_STATUS_LABEL[row.request_status] || row.request_status || '-'}
            </Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Fulfillment',
      dataIndex: 'fulfillment',
      key: 'fulfillment',
      width: 190,
      render: (value) => {
        const normalized = String(value || '').toUpperCase();
        return (
          <Tooltip title={normalized || '-'}>
            <Tag color={FULFILLMENT_COLOR[normalized] || 'default'}>
              {FULFILLMENT_LABEL[normalized] || normalized || '-'}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 120,
      render: (value) => {
        const tone = getSeverityTone(value);
        return <Tag color={severityColor[tone] || 'default'}>{String(value || '').toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 130,
      render: (value) => <Tag color={classificationColor[String(value || '').toLowerCase()] || 'default'}>{value}</Tag>,
    },
    {
      title: (
        <Tooltip title="Tempo acumulado em relacao ao SLA warning/critical por request.">
          <span>Aging</span>
        </Tooltip>
      ),
      key: 'aging',
      width: 260,
      render: (_, row) => {
        const warningMinutes = row?.metadata?.sla_warning_minutes;
        const criticalMinutes = row?.metadata?.sla_critical_minutes;
        const aging = computeAgingProgress({
          ageMinutes: row.age_minutes,
          warningMinutes,
          criticalMinutes,
        });

        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Text>{`${row.age_minutes} min`}</Text>
            <Progress
              percent={aging.percent}
              size="small"
              strokeColor={
                aging.status === 'critical'
                  ? '#cf1322'
                  : aging.status === 'warning'
                    ? '#d48806'
                    : '#389e0d'
              }
              showInfo={false}
            />
            <Text type="secondary">{`warn=${warningMinutes} | crit=${criticalMinutes}`}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Reason',
      dataIndex: 'reason_code',
      key: 'reason_code',
      width: 220,
      render: (value) => <Text code>{value}</Text>,
    },
    {
      title: (
        <Tooltip title="Resumo de bloqueios calculados na UI para evitar acao invalida.">
          <span>Bloqueios</span>
        </Tooltip>
      ),
      key: 'blockers',
      width: 260,
      render: (_, row) => {
        const blockedActions = Object.keys(ACTIONS)
          .map((actionKey) => ({ actionKey, availability: getActionAvailability(row, actionKey) }))
          .filter((item) => !item.availability.enabled);

        if (blockedActions.length === 0) {
          return <Tag color="green">Nenhum</Tag>;
        }

        return (
          <Space direction="vertical" size={2}>
            {blockedActions.slice(0, 2).map((item) => (
              <Tooltip key={item.actionKey} title={item.availability.blockers.join(' | ')}>
                <Tag color="red">{ACTIONS[item.actionKey].label}</Tag>
              </Tooltip>
            ))}
            {blockedActions.length > 2 ? <Text type="secondary">+{blockedActions.length - 2} bloqueios</Text> : null}
          </Space>
        );
      },
    },
    {
      title: 'Timeline',
      key: 'timeline',
      width: 110,
      render: (_, row) => (
        <Button icon={<HistoryOutlined />} size="small" onClick={() => openTimeline(row)}>
          Ver
        </Button>
      ),
    },
    {
      title: (
        <Tooltip title="Acoes manuais governadas com habilitacao contextual para reduzir erro operacional.">
          <span>Acoes manuais</span>
        </Tooltip>
      ),
      key: 'actions',
      fixed: 'right',
      width: 370,
      render: (_, row) => (
        <Space wrap>
          {renderActionButton(row, 'replay_safe')}
          {renderActionButton(row, 'mark_dead_letter')}
          {renderActionButton(row, 'reopen_dead_letter')}
          {renderActionButton(row, 'reclassify_recovery')}
          {renderActionButton(row, 'manual_reissue_prepare_payment')}
        </Space>
      ),
    },
  ];

  const stuckError = stuckQuery.isError
    ? buildApiErrorMessage(stuckQuery.error, 'Nao foi possivel carregar stuck requests.')
    : null;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>Recovery Operations Console</Title>
          <Text type="secondary">
            Console operacional admin-only para recovery SS-6 com acoes manuais governadas e timeline read-only.
          </Text>
        </div>

        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8} lg={6}>
              <Input
                placeholder="tenant_id"
                value={tenantId}
                onChange={(event) => setTenantId(event.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Select
                mode="multiple"
                placeholder="severity"
                options={[
                  { value: 'normal', label: 'normal' },
                  { value: 'warning', label: 'warning' },
                  { value: 'critical', label: 'critical' },
                ]}
                value={selectedSeverities}
                onChange={setSelectedSeverities}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Select
                mode="multiple"
                placeholder="classification"
                options={[
                  { value: 'transient', label: 'transient' },
                  { value: 'operator', label: 'operator' },
                  { value: 'terminal', label: 'terminal' },
                ]}
                value={selectedClassifications}
                onChange={setSelectedClassifications}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} md={8} lg={6}>
              <Select
                placeholder="fulfillment"
                options={fulfillmentOptions}
                value={selectedFulfillment || undefined}
                onChange={(value) => setSelectedFulfillment(value || '')}
                allowClear
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24}>
              <Space wrap>
                <Button icon={<ReloadOutlined />} onClick={refreshAll} loading={stuckQuery.isFetching || summaryQuery.isFetching || agingQuery.isFetching}>
                  Atualizar
                </Button>
                <Space>
                  <Switch checked={autoRefreshEnabled} onChange={setAutoRefreshEnabled} />
                  <Text>Auto refresh (120s, sem polling agressivo)</Text>
                </Space>
                <Text type="secondary">{lastRefreshAt ? `Ultima atualizacao: ${formatDateTime(lastRefreshAt)}` : 'Sem atualizacao manual recente'}</Text>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card>
              <Statistic title="Tracked Requests" value={summaryData.total_tracked_requests || 0} prefix={<ToolOutlined />} />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Space direction="vertical" size={4}>
                <Text strong>Severity</Text>
                <Space wrap>
                  <Tag color="green">normal: {severitySummary.normal || 0}</Tag>
                  <Tag color="gold">warning: {severitySummary.warning || 0}</Tag>
                  <Tag color="red">critical: {severitySummary.critical || 0}</Tag>
                </Space>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card>
              <Space direction="vertical" size={4}>
                <Text strong>Classification</Text>
                <Space wrap>
                  <Tag color="blue">transient: {classificationSummary.transient || 0}</Tag>
                  <Tag color="orange">operator: {classificationSummary.operator || 0}</Tag>
                  <Tag color="magenta">terminal: {classificationSummary.terminal || 0}</Tag>
                </Space>
              </Space>
            </Card>
          </Col>
        </Row>

        <Card
          title={(
            <Tooltip title="Buckets de aging por fulfillment para priorizacao operacional sem alterar regras de negocio.">
              <span>Aging Buckets Operacionais</span>
            </Tooltip>
          )}
        >
          <Space style={{ marginBottom: 12 }} wrap>
            <Tag color={agingSummary.warning_sla_breached_count > 0 ? 'gold' : 'green'}>
              warning breached: {agingSummary.warning_sla_breached_count || 0}
            </Tag>
            <Tag color={agingSummary.critical_sla_breached_count > 0 ? 'red' : 'green'}>
              critical breached: {agingSummary.critical_sla_breached_count || 0}
            </Tag>
          </Space>
          <Row gutter={[12, 12]}>
            {agingBuckets.length === 0 ? (
              <Col span={24}>
                <Text type="secondary">Sem buckets para os filtros atuais.</Text>
              </Col>
            ) : agingBuckets.map((bucket) => {
              const total = Number(bucket.total || 0);
              const critical = Number(bucket.critical_or_more || 0);
              const warning = Number(bucket.warning_to_critical || 0);
              const normal = Number(bucket.under_warning || 0);
              const criticalPercent = total > 0 ? Math.round((critical / total) * 100) : 0;

              return (
                <Col xs={24} md={12} lg={8} key={bucket.fulfillment_state}>
                  <Card size="small">
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Text strong>{bucket.fulfillment_state}</Text>
                      <Space wrap>
                        <Tag color="green">under: {normal}</Tag>
                        <Tag color="gold">warn: {warning}</Tag>
                        <Tag color="red">critical: {critical}</Tag>
                      </Space>
                      <Progress percent={criticalPercent} status={criticalPercent > 0 ? 'exception' : 'normal'} />
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>

        <Card title={`Stuck Requests (${filteredItems.length})`}>
          {stuckError ? (
            <Alert
              type="error"
              showIcon
              message="Falha ao carregar stuck requests"
              description={stuckError.message}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <Table
            rowKey={(row) => row.request_id}
            columns={columns}
            dataSource={filteredItems}
            loading={stuckQuery.isLoading || summaryQuery.isLoading || agingQuery.isLoading}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 1800 }}
            rowClassName={(row) => {
              const tone = getSeverityTone(row?.severity);
              if (tone === 'critical') {
                return 'recovery-row-critical';
              }
              if (tone === 'warning') {
                return 'recovery-row-warning';
              }
              return 'recovery-row-normal';
            }}
          />
        </Card>
      </Space>

      <RecoveryActionModal
        open={Boolean(actionContext)}
        context={actionContext}
        loading={operationMutation.isPending}
        onCancel={closeActionModal}
        onConfirm={executeManualAction}
      />

      <Drawer
        title={timelineRequest ? `Timeline read-only | request_id=${timelineRequest.request_id}` : 'Timeline read-only'}
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        width={1160}
      >
        {timelineError ? (
          <Alert type="error" showIcon message="Falha ao carregar timeline" description={timelineError} style={{ marginBottom: 16 }} />
        ) : null}

        <Table
          rowKey={(row, index) => `${row.occurred_at || 'event'}-${index}`}
          dataSource={timelineData}
          loading={timelineLoading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          columns={[
            {
              title: 'Occurred At',
              dataIndex: 'occurred_at',
              key: 'occurred_at',
              width: 170,
              render: (value) => formatDateTime(value),
            },
            {
              title: 'Category',
              dataIndex: 'category',
              key: 'category',
              width: 120,
              render: (value) => <Tag>{value || '-'}</Tag>,
            },
            {
              title: 'Severity',
              dataIndex: 'severity',
              key: 'severity',
              width: 120,
              render: (value) => {
                const tone = String(value || '').toLowerCase();
                const color = tone === 'critical' || tone === 'error' ? 'red' : tone === 'warning' ? 'gold' : 'blue';
                return <Tag color={color}>{String(value || '').toUpperCase()}</Tag>;
              },
            },
            {
              title: 'Correlation ID',
              key: 'correlation_id',
              width: 220,
              render: (_, row) => {
                const context = extractTimelineOperationalContext(row);
                return <Text code>{context.correlationId}</Text>;
              },
            },
            {
              title: 'Actor',
              key: 'actor',
              width: 180,
              render: (_, row) => {
                const context = extractTimelineOperationalContext(row);
                return <Text>{context.actor}</Text>;
              },
            },
            {
              title: 'Operation Type',
              key: 'operation_type',
              width: 220,
              render: (_, row) => {
                const context = extractTimelineOperationalContext(row);
                return <Text code>{context.operationType}</Text>;
              },
            },
            {
              title: 'Severity Snapshot',
              key: 'severity_snapshot',
              width: 170,
              render: (_, row) => {
                const context = extractTimelineOperationalContext(row);
                return <Tag>{String(context.severitySnapshot || '-').toUpperCase()}</Tag>;
              },
            },
            {
              title: 'Event Type',
              dataIndex: 'event_type',
              key: 'event_type',
              width: 180,
              render: (value) => <Text code>{value || '-'}</Text>,
            },
            {
              title: 'Message',
              dataIndex: 'message',
              key: 'message',
            },
          ]}
        />
      </Drawer>

      <style>
        {`
          .recovery-row-critical td {
            background: #fff1f0 !important;
          }
          .recovery-row-warning td {
            background: #fffbe6 !important;
          }
          .recovery-row-normal td {
            background: #f6ffed !important;
          }
        `}
      </style>
    </div>
  );
};

export default PlatformRecoveryOperationsConsole;
